import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, message, Card, Table, Popconfirm, Tag, Select, InputNumber, Upload, Avatar } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, MinusCircleOutlined, LinkOutlined, UploadOutlined } from '@ant-design/icons';
import { samagriApi, samagriTagAPI, bidhiSamagriAssociationApi, pujaSamagriAssociationApi, karmakandaAPI, pujaApi, API_URL } from '../services/api';
import type { Samagri, Bidhi, Puja, SamagriTags } from '../types';
import SamagriParser from '../components/SamagriParser';
import { appendSamagriTagIdsToFormData, normalizeSamagriTagIds } from '../utils/samagriTags';

const SamagriPage: React.FC = () => {
  const [samagris, setSamagris] = useState<Samagri[]>([]);
  const [loading, setLoading] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [linkModalVisible, setLinkModalVisible] = useState(false);
  const [parserModalVisible, setParserModalVisible] = useState(false);
  const [linkType, setLinkType] = useState<'bidhi' | 'puja'>('bidhi');
  const [selectedSamagri, setSelectedSamagri] = useState<Samagri | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [bidhis, setBidhis] = useState<Bidhi[]>([]);
  const [pujas, setPujas] = useState<Puja[]>([]);
  const [samagriTags, setSamagriTags] = useState<SamagriTags[]>([]);
  const [loadingSamagriTags, setLoadingSamagriTags] = useState(false);
  const [creatingSamagriTag, setCreatingSamagriTag] = useState(false);
  const [samagriTagSearchValue, setSamagriTagSearchValue] = useState('');

  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [linkForm] = Form.useForm();
  const [fileList, setFileList] = useState<any[]>([]);
  const [editFileList, setEditFileList] = useState<any[]>([]);

  useEffect(() => {
    fetchSamagris();
    fetchBidhis();
    fetchPujas();
    void loadSamagriTags();
  }, []);

  const loadSamagriTags = async () => {
    setLoadingSamagriTags(true);
    try {
      const response = await samagriTagAPI.getAll();
      setSamagriTags(response.data ?? []);
    } catch (error) {
      console.error('Error loading samagri tags:', error);
    } finally {
      setLoadingSamagriTags(false);
    }
  };

  const createSamagriTag = async (name: string): Promise<number | null> => {
    try {
      const response = await samagriTagAPI.createTag({ name });
      setSamagriTags((prev) => [...prev, response.data]);
      return response.data.id;
    } catch (error) {
      console.error('Error creating samagri tag:', error);
      message.error('Failed to create tag');
      return null;
    }
  };

  const handleCreateSamagriTag = async () => {
    const name = samagriTagSearchValue.trim();
    if (!name) return;

    setCreatingSamagriTag(true);
    try {
      const tagId = await createSamagriTag(name);
      if (tagId != null) {
        const activeForm = editModalVisible ? editForm : addForm;
        const current = normalizeSamagriTagIds(activeForm.getFieldValue('tagIds'));
        if (!current.includes(tagId)) {
          activeForm.setFieldValue('tagIds', [...current, tagId]);
        }
      }
      setSamagriTagSearchValue('');
    } finally {
      setCreatingSamagriTag(false);
    }
  };

  const fetchBidhis = async () => {
    try {
      const response = await karmakandaAPI.getAllBidhis();
      setBidhis(response.data);
    } catch (error) {
      console.error('Error fetching bidhis:', error);
    }
  };

  const fetchPujas = async () => {
    try {
      const response = await pujaApi.getAll();
      setPujas(response.data);
    } catch (error) {
      console.error('Error fetching pujas:', error);
    }
  };

  const fetchSamagris = async () => {
    setLoading(true);
    try {
      const response = await samagriApi.getAll();
      setSamagris(response.data);
    } catch (error) {
      console.error('Error fetching samagris:', error);
      message.error('Failed to fetch samagris');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (values: any) => {
    try {
      const tagIds = normalizeSamagriTagIds(values.tagIds);
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('desc', values.desc);
      if (values.alternativeNames) {
        values.alternativeNames.forEach((name: string) => {
          formData.append('alternativeNames[]', name);
        });
      }
      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append('profilePicture', fileList[0].originFileObj);
      }
      appendSamagriTagIdsToFormData(formData, tagIds);

      if (fileList.length > 0 && fileList[0].originFileObj) {
        await samagriApi.create(formData);
      } else {
        await samagriApi.create({
          name: values.name,
          desc: values.desc,
          alternativeNames: values.alternativeNames,
          tagIds,
        });
      }
      message.success('Samagri created successfully');
      setAddModalVisible(false);
      addForm.resetFields();
      setFileList([]);
      fetchSamagris();
    } catch (error) {
      console.error('Error creating samagri:', error);
      message.error('Failed to create samagri');
    }
  };

  const handleEdit = async (values: any) => {
    if (!selectedSamagri?.id) return;
    try {
      const tagIds = normalizeSamagriTagIds(values.tagIds);
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('desc', values.desc);
      if (values.alternativeNames) {
        values.alternativeNames.forEach((name: string) => {
          formData.append('alternativeNames[]', name);
        });
      }
      if (editFileList.length > 0 && editFileList[0].originFileObj) {
        formData.append('profilePicture', editFileList[0].originFileObj);
      }
      appendSamagriTagIdsToFormData(formData, tagIds);

      if (editFileList.length > 0 && editFileList[0].originFileObj) {
        await samagriApi.update(selectedSamagri.id, formData);
      } else {
        await samagriApi.update(selectedSamagri.id, {
          name: values.name,
          desc: values.desc,
          alternativeNames: values.alternativeNames,
          tagIds,
        });
      }
      message.success('Samagri updated successfully');
      setEditModalVisible(false);
      editForm.resetFields();
      setEditFileList([]);
      fetchSamagris();
    } catch (error) {
      console.error('Error updating samagri:', error);
      message.error('Failed to update samagri');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await samagriApi.delete(id);
      message.success('Samagri deleted successfully');
      fetchSamagris();
    } catch (error) {
      console.error('Error deleting samagri:', error);
      message.error('Failed to delete samagri');
    }
  };

  const handleLink = async (values: any) => {
    if (!selectedSamagri?.id) return;
    try {
      if (linkType === 'bidhi') {
        await bidhiSamagriAssociationApi.create({
          bidhiId: values.targetId,
          samagriId: selectedSamagri.id,
          quantity: values.quantity,
          unit: values.unit,
        });
        message.success('Samagri linked to Bidhi successfully');
      } else {
        await pujaSamagriAssociationApi.create({
          pujaId: values.targetId,
          samagriId: selectedSamagri.id,
          quantity: values.quantity,
          unit: values.unit,
        });
        message.success('Samagri linked to Puja successfully');
      }
      setLinkModalVisible(false);
      linkForm.resetFields();
    } catch (error) {
      console.error('Error linking samagri:', error);
      message.error('Failed to link samagri');
    }
  };

  const openLinkModal = (samagri: Samagri, type: 'bidhi' | 'puja') => {
    setSelectedSamagri(samagri);
    setLinkType(type);
    linkForm.resetFields();
    linkForm.setFieldsValue({ quantity: 1 });
    setLinkModalVisible(true);
  };

  const handleBulkImport = async (samagris: any[]) => {
    try {
      for (const samagri of samagris) {
        await samagriApi.create(samagri);
      }
      message.success(`Imported ${samagris.length} samagris successfully`);
      setParserModalVisible(false);
      fetchSamagris();
    } catch (error) {
      console.error('Error importing samagris:', error);
      message.error('Failed to import samagris');
    }
  };

  const filteredSamagris = samagris.filter(s => {
    const q = searchQuery.toLowerCase();
    return s.name.toLowerCase().includes(q) || 
           s.desc.toLowerCase().includes(q) ||
           s.alternativeNames?.some(n => n.toLowerCase().includes(q)) ||
           s.tags?.some((tag) => tag.name.toLowerCase().includes(q));
  });

  const renderSamagriTagSelect = () => (
    <Form.Item name="tagIds" label="Tags">
      <Select
        mode="multiple"
        allowClear
        showSearch
        placeholder="Select or create tags"
        loading={loadingSamagriTags}
        optionFilterProp="label"
        onSearch={setSamagriTagSearchValue}
        notFoundContent={
          samagriTagSearchValue.trim() ? (
            <Button
              type="text"
              icon={<PlusOutlined />}
              onClick={() => void handleCreateSamagriTag()}
              loading={creatingSamagriTag}
              block
            >
              Create "{samagriTagSearchValue.trim()}"
            </Button>
          ) : null
        }
        options={samagriTags.map((tag) => ({
          value: tag.id,
          label: tag.name,
        }))}
      />
    </Form.Item>
  );

  const columns = [
    {
      title: 'Picture',
      dataIndex: 'profilePicture',
      key: 'profilePicture',
      width: 80,
      render: (pic: string) => (
        <Avatar src={pic ? `${API_URL}/uploads/samagri/${pic}` : undefined} size={48}>{!pic && 'S'}</Avatar>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Description',
      dataIndex: 'desc',
      key: 'desc',
    },
    {
      title: 'Alternative Names',
      dataIndex: 'alternativeNames',
      key: 'alternativeNames',
      render: (names: string[]) => (
        <>
          {names?.map((name, idx) => (
            <Tag key={idx}>{name}</Tag>
          ))}
        </>
      ),
    },
    {
      title: 'Tags',
      key: 'tags',
      render: (_: unknown, record: Samagri) =>
        record.tags?.length ? (
          <>
            {record.tags.map((tag) => (
              <Tag key={tag.id} color="blue">
                {tag.name}
              </Tag>
            ))}
          </>
        ) : (
          '—'
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Samagri) => (
        <>
          <Button
            type="link"
            icon={<LinkOutlined />}
            onClick={() => openLinkModal(record, 'bidhi')}
          >
            Bidhi
          </Button>
          <Button
            type="link"
            icon={<LinkOutlined />}
            onClick={() => openLinkModal(record, 'puja')}
          >
            Puja
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedSamagri(record);
              editForm.setFieldsValue({
                name: record.name,
                desc: record.desc,
                alternativeNames: record.alternativeNames || [],
                tagIds: record.tags?.map((tag) => tag.id) ?? [],
              });
              setEditFileList(record.profilePicture ? [{ uid: '-1', name: 'image', status: 'done', url: `${API_URL}/uploads/samagri/${record.profilePicture}` }] : []);
              setEditModalVisible(true);
            }}
          />
          <Popconfirm
            title="Delete samagri?"
            onConfirm={() => handleDelete(record.id!)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2>Samagri Management</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button icon={<UploadOutlined />} onClick={() => setParserModalVisible(true)}>
            Import
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalVisible(true)}>
            Add Samagri
          </Button>
        </div>
      </div>

      <Input
        placeholder="Search by name, description, alternative names, or tags..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        allowClear
        style={{ marginBottom: 16, width: 400 }}
      />

      <Card>
        <Table
          dataSource={filteredSamagris}
          columns={columns}
          rowKey="id"
          loading={loading}
        />
      </Card>

      <Modal
        title="Add Samagri"
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          addForm.resetFields();
          setFileList([]);
        }}
        onOk={() => addForm.submit()}
      >
        <Form form={addForm} onFinish={handleAdd} layout="vertical">
          <Form.Item label="Profile Picture">
            <Upload
              listType="picture-card"
              fileList={fileList}
              beforeUpload={() => false}
              onChange={({ fileList }) => setFileList(fileList.slice(-1))}
              maxCount={1}
            >
              {fileList.length === 0 && <div><PlusOutlined /><div>Upload</div></div>}
            </Upload>
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="desc" label="Description" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          {renderSamagriTagSelect()}
          <Form.List name="alternativeNames">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Form.Item key={field.key} label={field.key === 0 ? 'Alternative Names' : ''}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Form.Item {...field} noStyle>
                        <Input placeholder="Alternative name" />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(field.name)}
                      />
                    </div>
                  </Form.Item>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Alternative Name
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      <Modal
        title="Edit Samagri"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
          setEditFileList([]);
        }}
        onOk={() => editForm.submit()}
      >
        <Form form={editForm} onFinish={handleEdit} layout="vertical">
          <Form.Item label="Profile Picture">
            <Upload
              listType="picture-card"
              fileList={editFileList}
              beforeUpload={() => false}
              onChange={({ fileList }) => setEditFileList(fileList.slice(-1))}
              maxCount={1}
            >
              {editFileList.length === 0 && <div><PlusOutlined /><div>Upload</div></div>}
            </Upload>
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="desc" label="Description" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          {renderSamagriTagSelect()}
          <Form.List name="alternativeNames">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Form.Item key={field.key} label={field.key === 0 ? 'Alternative Names' : ''}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Form.Item {...field} noStyle>
                        <Input placeholder="Alternative name" />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(field.name)}
                      />
                    </div>
                  </Form.Item>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Alternative Name
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      <Modal
        title={`Link Samagri to ${linkType === 'bidhi' ? 'Bidhi' : 'Puja'}`}
        open={linkModalVisible}
        onCancel={() => {
          setLinkModalVisible(false);
          linkForm.resetFields();
        }}
        onOk={() => linkForm.submit()}
      >
        <Form form={linkForm} onFinish={handleLink} layout="vertical">
          <Form.Item name="targetId" label={linkType === 'bidhi' ? 'Bidhi' : 'Puja'} rules={[{ required: true }]}>
            <Select
              showSearch
              placeholder={`Select ${linkType}`}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={(linkType === 'bidhi' ? bidhis : pujas).map(item => ({
                value: item.id,
                label: item.name,
              }))}
            />
          </Form.Item>
          <Form.Item name="quantity" label="Quantity" rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="unit" label="Unit" rules={[{ required: true }]}>
            <Input placeholder="e.g., pieces, grams, kg" />
          </Form.Item>
        </Form>
      </Modal>

      <SamagriParser
        visible={parserModalVisible}
        onCancel={() => setParserModalVisible(false)}
        onImport={handleBulkImport}
      />
    </div>
  );
};

export default SamagriPage;
